// src/auth/ContactLoginScreen.tsx
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useContactAuth } from "../../hooks/useContactAuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Typography, Link, Paper } from "@mui/material";
import { useBrandingContext } from "context/BrandingContext";
import { useLogoForScheme } from "utils/themeContextHelper";
import ContactLoginForm from "components/auth/ContactLoginForm";

const PAY_LINK_PATH = /^\/(?:pay\/)?([^/]+)(?:\/complete)?\/?$/;

export default function ContactLoginScreen() {
  const { t } = useTranslation();
  const { platform } = useBrandingContext();
  const logo = useLogoForScheme();
  const supportEmail = platform?.email || "support@outpave.com";
  const { isContactAuthenticated } = useContactAuth();

  const navigate = useNavigate();
  const location = useLocation();

  // If they were redirected to /contact/login, `location.state.from` holds the original URL.
  const from = (location.state as { from?: Location })?.from;

  const fromPath = from?.pathname
    ? `${from.pathname}${from.search ?? ""}${from.hash ?? ""}`
    : "/pay-invoice/checkout";

  useEffect(() => {
    if (isContactAuthenticated) {
      navigate(fromPath, { replace: true });
    }
  }, [isContactAuthenticated, navigate, fromPath]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "var(--color-bg-page)",
      }}
    >
      <Paper
        sx={{ padding: "var(--space-9)", width: "100%", maxWidth: "448px" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginBottom: "var(--space-9)",
          }}
        >
          <Box
            component="img"
            src={logo}
            alt={t("Company Logo")}
            sx={{ height: "48px" }}
          />
        </Box>

        <Typography
          variant="h4"
          component="h1"
          sx={{ textAlign: "center", marginBottom: "var(--space-7)" }}
        >
          {t("Sign-in")}
        </Typography>

        <ContactLoginForm
          paymentLinkSlug={paymentLinkSlugOf(from?.pathname)}
          onAuthenticated={() => navigate(fromPath, { replace: true })}
        />

        <Box sx={{ textAlign: "center", marginTop: "var(--space-5)" }}>
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {t("Need help?")}{" "}
            <Link href={`mailto:${supportEmail}`}>{t("Contact Support")}</Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

/* Support both the main site's /pay/:slug and the payment host's /:slug routes. */
function paymentLinkSlugOf(pathname?: string): string | undefined {
  if (!pathname) return undefined;

  const [, slug] = pathname.match(PAY_LINK_PATH) || [];

  return slug && !["contact", "pay-invoice"].includes(slug) ? slug : undefined;
}
